import { FulfillmentOrderStatus } from '../../../graphql'
import { PICKABLE_FO_STATUSES } from './constants'

/**
 * What makes an order pickable at all, ignoring what else is chosen.
 *
 * Structural rather than the generated row type: the same rules apply wherever
 * an order is offered for picking, and the fulfillment order detail page has no
 * destination key to give.
 */
export type PickableFulfillmentOrder = {
	status: FulfillmentOrderStatus,
	/** Open runs only. Non-empty means the order is already spoken for. */
	pickOrders: { id: string, pickOrderNumber: number }[],
	lineItems: { quantity: number, quantityFulfilled: number }[],
}

export type SelectableFulfillmentOrder = PickableFulfillmentOrder & {
	id: string,
	/** inventory-api's own destination key. Equal keys may share a run. */
	destinationKey: string,
}

/**
 * Whether an order could be picked by hand at all.
 *
 * Three conditions, all mirroring inventory-api:
 *
 * - **Status** — submitted for fulfillment; `_load_eligible_fulfillment_orders`
 *   rejects anything else.
 * - **Not already being picked** — `pickOrders` is asked for open runs only, so
 *   a finished run never counts, however it ended. A run that left a line open
 *   hands the order back with work still on it and it becomes pickable again; a
 *   run that shorted the line completes the order instead, and the status check
 *   above is what stops it. Which statuses count as open is inventory-api's to
 *   decide, which is why this reads a list it filtered rather than a status of
 *   its own.
 * - **Outstanding work** — `_create_pick_order_lines` builds lines only from
 *   line items with `quantity - quantity_fulfilled > 0`, so an order already
 *   fulfilled in full would produce a pick order with nothing on it.
 */
export function isPickableFulfillmentOrder( order: PickableFulfillmentOrder ): boolean {
	return PICKABLE_FO_STATUSES.includes( order.status ) &&
		order.pickOrders.length === 0 &&
		order.lineItems.some( ( lineItem ) => lineItem.quantity > lineItem.quantityFulfilled )
}

/**
 * Whether a row may be checked, given what is already checked.
 *
 * `anchorKey` is the destination of the first order chosen, and null while
 * nothing constrains the run yet. The comparison is exact string equality
 * because `destinationKey` is inventory-api's own key rendered — the same
 * function the creation invariant enforces — rather than a second definition
 * assembled here from an address and a trailer quadrant.
 */
export function canSelectForPickOrder(
	order: SelectableFulfillmentOrder,
	selectedIds: ReadonlySet<string>,
	anchorKey: string | null,
): boolean {
	// Something already checked stays checkable, so a change of anchor can never
	// strand a box the user is then unable to clear.
	if ( selectedIds.has( order.id ) ) {
		return true
	}
	if ( !isPickableFulfillmentOrder( order ) ) {
		return false
	}
	return anchorKey === null || order.destinationKey === anchorKey
}

/**
 * The destination every other order in the run has to match: the first one
 * chosen. Clearing it re-anchors on whatever is left rather than emptying the
 * selection.
 */
export function anchorDestinationKey( selected: readonly { destinationKey: string }[] ): string | null {
	return selected[ 0 ]?.destinationKey ?? null
}

/**
 * The ids a Data Grid selection model names.
 *
 * "Select all" arrives as an `exclude` model, which cannot be enumerated
 * against a server-paged grid — there is no way to name rows the client has
 * never loaded. It is read as "every row currently loaded, minus these",
 * which is what the gesture means on a page of results.
 */
export function idsFromSelectionModel(
	model: { type: 'include' | 'exclude', ids: ReadonlySet<unknown> },
	loadedIds: readonly string[],
): string[] {
	const named = new Set( Array.from( model.ids ).map( String ) )
	if ( model.type === 'include' ) {
		return Array.from( named )
	}
	return loadedIds.filter( ( id ) => !named.has( id ) )
}
